import typia from 'typia';
import { dispatchAction } from '@ardoq/rxbeach';
import { SidebarPanelContentIds } from 'canvas/sidebarPanel/types';
import { openSidebarPanel, closeSidebarPanel, } from 'canvas/sidebarPanel/actions';
import { WHEN_TO_USE, VIEW_SIDE_PANEL_NAV_CONTEXT_DESCRIPTION, } from './descriptions';
import { AiControlledCommandId } from 'aiControlledCommands/aiControlledCommandId';
import { defineAiControlledCommand } from 'aiControlledCommands/defineAiControlledCommand';
import { VIEWPOINT_DOMAIN } from 'aiControlledCommands/viewpoint/domain';
import type { AiControlledCommand } from 'aiControlledCommands/types';
// The view-settings side panels the assistant is allowed to open.
type NavigableSidebarPanel = SidebarPanelContentIds.VIEW_SETTINGS | SidebarPanelContentIds.VIEW_SETTINGS_PERSPECTIVE_GROUPS | SidebarPanelContentIds.VIEW_SETTINGS_PERSPECTIVE_LABELS | SidebarPanelContentIds.VIEW_SETTINGS_PERSPECTIVE_CONDITIONAL_FORMAT;
// typia compiles the JSDoc here into the command's JSON schema, so these
// comments are LLM-facing — keep them accurate.
export type ShowViewSidePanelContentPayload = {
    /**
     * The panel to open:
     * - `VIEW_SETTINGS` — view options (choose the view and its settings);
     * - `VIEW_SETTINGS_PERSPECTIVE_GROUPS` — grouping;
     * - `VIEW_SETTINGS_PERSPECTIVE_LABELS` — label formatting;
     * - `VIEW_SETTINGS_PERSPECTIVE_CONDITIONAL_FORMAT` — conditional formatting.
     * Use `null` to close the side panel.
     */
    panel: NavigableSidebarPanel | null;
};
type ShowViewSidePanelContentContext = {
    currentPanel: SidebarPanelContentIds | null;
};
export const buildUiNavigationCommand = (currentPanel: SidebarPanelContentIds | null): AiControlledCommand<typeof navigationCommandDefinition> => ({
    ...navigationCommandDefinition,
    context: { currentPanel },
});
export const executeShowViewSidePanelContent = (payload: ShowViewSidePanelContentPayload): void => {
    dispatchAction(payload.panel === null
        ? closeSidebarPanel()
        : openSidebarPanel(payload.panel));
};
export const navigationCommandDefinition = defineAiControlledCommand<ShowViewSidePanelContentPayload, ShowViewSidePanelContentContext>({
    name: AiControlledCommandId.SHOW_VIEW_SIDE_PANEL_CONTENT,
    requiresAppShell: true,
    domain: VIEWPOINT_DOMAIN,
    description: WHEN_TO_USE,
    parameters: {
        version: "3.1",
        components: {
            schemas: {
                ShowViewSidePanelContentPayload: {
                    type: "object",
                    properties: {
                        panel: {
                            oneOf: [
                                {
                                    type: "null"
                                },
                                {
                                    "const": "VIEW_SETTINGS"
                                },
                                {
                                    "const": "VIEW_SETTINGS_PERSPECTIVE_GROUPS"
                                },
                                {
                                    "const": "VIEW_SETTINGS_PERSPECTIVE_CONDITIONAL_FORMAT"
                                },
                                {
                                    "const": "VIEW_SETTINGS_PERSPECTIVE_LABELS"
                                }
                            ],
                            description: "The panel to open:\n- `VIEW_SETTINGS` \u2014 view options (choose the view and its settings);\n- `VIEW_SETTINGS_PERSPECTIVE_GROUPS` \u2014 grouping;\n- `VIEW_SETTINGS_PERSPECTIVE_LABELS` \u2014 label formatting;\n- `VIEW_SETTINGS_PERSPECTIVE_CONDITIONAL_FORMAT` \u2014 conditional formatting.\nUse `null` to close the side panel."
                        }
                    },
                    required: [
                        "panel"
                    ]
                }
            }
        },
        schema: {
            $ref: "#/components/schemas/ShowViewSidePanelContentPayload"
        }
    } as import("typia").IJsonSchemaUnit<"3.1">,
    contextDescription: VIEW_SIDE_PANEL_NAV_CONTEXT_DESCRIPTION,
    isContext: (context): context is ShowViewSidePanelContentContext => (() => { const _io0 = (input: any): boolean => null === input.currentPanel || "CONTEXT_SWITCHER" === input.currentPanel || "CREATE_REFERENCES" === input.currentPanel || "VIEWPOINT_SWITCHER" === input.currentPanel || "DETAILS" === input.currentPanel || "DATASETS" === input.currentPanel || "VIEW_SETTINGS" === input.currentPanel || "VIEW_SETTINGS_PERSPECTIVE_GROUPS" === input.currentPanel || "VIEW_SETTINGS_PERSPECTIVE_CONDITIONAL_FORMAT" === input.currentPanel || "VIEW_SETTINGS_PERSPECTIVE_LABELS" === input.currentPanel; return (input: any): input is ShowViewSidePanelContentContext => "object" === typeof input && null !== input && _io0(input); })()(context),
    validate: rawPayload => (() => { const _io0 = (input: any): boolean => null === input.panel || "VIEW_SETTINGS" === input.panel || "VIEW_SETTINGS_PERSPECTIVE_GROUPS" === input.panel || "VIEW_SETTINGS_PERSPECTIVE_CONDITIONAL_FORMAT" === input.panel || "VIEW_SETTINGS_PERSPECTIVE_LABELS" === input.panel; return (input: any): input is ShowViewSidePanelContentPayload => "object" === typeof input && null !== input && _io0(input); })()(rawPayload) ? { status: 'valid', payload: rawPayload }
        : {
            status: 'rejected',
            reason: 'The AI returned an invalid panel to open.',
        },
    execute: executeShowViewSidePanelContent,
    isApplied: ({ panel }, { currentPanel }) => panel === currentPanel,
});
